import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';

import {
  useDroppable
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';

import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import MaskedInput from 'react-text-mask'
import createNumberMask from 'text-mask-addons/dist/createNumberMask'
import {DebounceInput} from 'react-debounce-input';
var _ = require('lodash');

const axios = require('axios').default;
import { toast } from 'react-toastify';

import { Badge, Button, Menu, TextInput, Tooltip, DotsGrid, DotsVertical, InfoCircle, Plus, Trash01 } from '@opendate-app/setlist';

import TicketTypeRow from './TicketTypeRow';
import TicketTypeRowV2, { TicketTypeTableHeader } from './TicketTypeRowV2';
import './TicketTypeRowV2.css';

const parseFloatFromString = (value) => {
  if(typeof(value) === "number"){
    return value;
  }

  return parseFloat(value.split(",").join(""));
}

let debouncedUpdateTicketGroup;

const TicketGroup = ({
  csrfToken,
  team,
  currentUser,
  confirm,
  ticketGroup,
  fetchNewTicketType,
  ticketTypeIsSeated,
  fetchTicketTypeForEdit,
  depth,
  ticketGroupChanged,
  ticketTypesChanged,
  parentTicketGroup,
  updateConfirm,
  provided,
  snapshot,
  canAddSubGroup,
  showHeader,
  showSubGroupHeaders = false,
  rootTicketGroup,
  redesignEnabled
}) => {
  const updateSubGroupPosition = (ticketGroup) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.patch("/teams/" + team.id + "/ticket_groups/" + ticketGroup.id, {
        ticket_group: {
          position: ticketGroup.position
        }
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const addSubGroup = () => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.post(`/teams/${team.id}/ticket_groups`, {
        ticket_group: {
          calendar_event_id: confirm.id,
          parent_id: ticketGroup.id
        }
      })
      .then(({ data }) => {
        var updated = {
          ...ticketGroup,
          sub_groups: [...ticketGroup.sub_groups, data]
        }

        ticketGroupChanged(updated);
      });
  };

  const updateTicketGroup = (ticketGroup) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.put(`/teams/${team.id}/ticket_groups/${ticketGroup.id}`, {
        ticket_group: {
          name: ticketGroup.name,
          capacity: ticketGroup.capacity ? parseFloatFromString(ticketGroup.capacity) : ""
        }
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  const deleteTicketGroup = (ticketGroup) => {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    return axios.delete(`/teams/${team.id}/ticket_groups/${ticketGroup.id}`)
      .then(({ data }) => {
        var updated = {
          ...parentTicketGroup,
          sub_groups: parentTicketGroup.sub_groups.filter((subGroup) => subGroup.id !== ticketGroup.id)
        }

        ticketGroupChanged(updated);
      })
      .catch((error) => {
        toast.error(error.response.data.join(", "), {
          position: toast.POSITION.TOP_CENTER,
          draggable: false,
          closeOnClick: false,
          autoClose: 5000,
          hideProgressBar: true
        });
      });
  };

  useEffect(() => {
    debouncedUpdateTicketGroup = _.debounce((ticketGroup) => {
      updateTicketGroup(ticketGroup);
    }, 300);
  }, []);

  const isEven = (depth % 2 === 0);
  const groupBorder = isEven ? "1px solid var(--sl-border-secondary)" : "1px solid var(--sl-bg-brand-secondary)";
  const capacityWarning = "This group's capacity is higher than some of its parent groups.";

  const { setNodeRef } = useDroppable({
    id: `ticket-group-${ticketGroup.id}`,
    data: {
      ticketGroupId: ticketGroup.id
    }
  });

  const findTicketGroupById = (currentGroup, targetId) => {
    if (currentGroup.id === targetId) {
      return currentGroup;
    }

    for (const subGroup of currentGroup.sub_groups) {
      const found = findTicketGroupById(subGroup, targetId);
      if (found) return found;
    }

    return null;
  };

  const hasParentTicketGroupWithLowerCapacity = (ticketGroup) => {
    if (ticketGroup.parent_id === null) {
      return false;
    }

    let currentCapacity = parseFloatFromString(ticketGroup.capacity || 0);
    let currentParentId = ticketGroup.parent_id;

    while (currentParentId) {
      const parentGroup = findTicketGroupById(rootTicketGroup, currentParentId);
      if (!parentGroup) break;

      const parentCapacity = parseFloatFromString(parentGroup.capacity || 0);
      if (parentCapacity < currentCapacity) {
        return true;
      }

      currentParentId = parentGroup.parent_id;
    }

    return false;
  };
  const currentlyHasLowerCapacityThanSomeParentGroup = hasParentTicketGroupWithLowerCapacity(ticketGroup);
  const sortedTicketTypes = [...ticketGroup.ticket_types].sort((a, b) => a.position - b.position);
  const ticketTypeIds = new Set(ticketGroup.ticket_types.map((tt) => tt.id));
  const v2TicketTypes = sortedTicketTypes.filter((tt) => !ticketTypeIds.has(tt.presale_parent_id));
  const lockedPool = Boolean(ticketGroup.presale_pool || ticketGroup.price_pool || ticketGroup.pass_holder_pool);

  return (
    <div className='row' ref={provided.innerRef} {...provided.draggableProps}>
      <div className='col-12'>
        <div className='card mb-0'
              style={{
                "margin": (depth === 0 ? "0px" : "16px"),
                "border": groupBorder,
                "backgroundColor": "var(--sl-bg-primary)"
              }}>
          {showHeader ? (
            <div className='card-header border-0 d-flex flex-column flex-md-row justify-content-between align-items-stretch align-items-md-center'
                  style={{
                    "padding": "16px 16px",
                    "backgroundColor": isEven ? "var(--sl-bg-primary)" : "var(--sl-bg-brand-primary)"
                  }}>
              <div className='d-flex flex-column flex-md-row align-items-stretch align-items-md-center'>
                {ticketGroup.root ? (
                  <p className='mb-2 mb-md-0'
                      style={{"fontSize": "16px", "fontWeight": "600", "whiteSpace": (redesignEnabled ? "nowrap" : undefined), "marginRight": (redesignEnabled ? "16px" : undefined)}}>
                    {redesignEnabled ? "Event Capacity" : ticketGroup.name}
                  </p>
                ) : (
                  <div className="d-flex align-items-center">
                    <a {...provided.dragHandleProps}
                      onClick={
                        (e) => {
                          e.preventDefault();
                        }
                      }
                      href="#"
                      aria-label="Reorder group"
                      className="d-inline-flex align-items-center"
                      style={{"color": "var(--sl-text-disabled)"}}>
                      <DotsGrid width={16} height={16} aria-hidden="true" />
                    </a>
                    <div style={{
                           "flex": "1 1 auto",
                           "maxWidth": "320px",
                           "minWidth": (lockedPool ? "240px" : undefined),
                           "marginLeft": "8px"
                         }}>
                      <TextInput as={DebounceInput}
                            type="text"
                            aria-label="Group name"
                            disabled={lockedPool}
                            debounceTimeout={300}
                            placeholder="Enter group name"
                            value={ticketGroup.name || ""}
                            onChange={
                              (e) => {
                                var updated = {
                                  ...ticketGroup,
                                  name: e.target.value
                                }

                                ticketGroupChanged(updated);
                                updateTicketGroup(updated);
                              }
                            } />
                    </div>
                    {lockedPool ? (
                      <span style={{ "flexShrink": 0, "whiteSpace": "nowrap", "marginLeft": "var(--setlist-spacing-lg)", "marginRight": "var(--setlist-spacing-lg)" }}>
                        <Badge color="brand" size="md">{ticketGroup.pass_holder_pool ? "Pass holders per date" : !ticketGroup.price_pool ? "Pre-sale pool" : ticketGroup.ticket_types.some((tt) => tt.seating_chart_category_key) ? "Section prices" : "Ticket prices"}</Badge>
                      </span>
                    ) : null}
                  </div>
                )}
                <div className="mt-2 mt-md-0 w-100 w-md-auto" style={{"minWidth": "120px"}}>
                  <TextInput as={MaskedInput}
                              type="text"
                              aria-label="Ticket quantity"
                              disabled={lockedPool}
                              error={currentlyHasLowerCapacityThanSomeParentGroup ? (
                                <span className="sr-only">{capacityWarning}</span>
                              ) : undefined}
                              trailingText={currentlyHasLowerCapacityThanSomeParentGroup ? (
                                <Tooltip title={capacityWarning} placement="top" maxWidth={200}>
                                  <span className="d-inline-flex" tabIndex={0} aria-label="About capacity warning">
                                    <InfoCircle width={16} height={16} aria-hidden="true" />
                                  </span>
                                </Tooltip>
                              ) : undefined}
                              placeholder={"Ticket QTY"}
                              mask={createNumberMask({
                                prefix: "",
                                allowDecimal: false,
                                integerLimit: 10,
                                decimalLimit: 0
                              })}
                              value={ticketGroup.capacity || ""}
                              onChange={
                                (e) => {
                                  var updated = {
                                    ...ticketGroup,
                                    capacity: e.target.value
                                  };

                                  if(ticketGroup.root){
                                    updateConfirm({
                                      ...confirm,
                                      venue_capacity: updated.capacity
                                    });
                                  }

                                  ticketGroupChanged(updated);
                                  debouncedUpdateTicketGroup(updated);
                                }
                              } />
                </div>
              </div>
              <div className="d-flex align-items-center mt-3 mt-md-0">
                {lockedPool ? null : (
                  <Button variant="secondary-color"
                          onClick={() => fetchNewTicketType(csrfToken, team, confirm, ticketGroup, null)}>
                    Add Ticket
                  </Button>
                )}
                {!ticketGroup.root && !lockedPool ? (
                  <span style={{"marginLeft": "var(--setlist-spacing-lg)"}}>
                    <Menu placement="bottom-end"
                          items={[
                            {
                              label: "Delete Sub-group",
                              icon: Trash01,
                              onClick: () => deleteTicketGroup(ticketGroup)
                            }
                          ]}
                          trigger={(
                            <Button variant="tertiary"
                                    icon={DotsVertical}
                                    aria-label={`Actions for ${ticketGroup.name || "sub-group"}`} />
                          )} />
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}
          <div className="row">
            <div className="col-12">
              <div className='table-responsive responsive-card-table-wrapper'
                    style={{
                      "paddingBottom": (ticketGroup.ticket_types.length === 0 ? "48px" : "0px"),
                      "borderBottom": (ticketGroup.ticket_types.length === 0 && depth < 2 ? "1px solid var(--sl-border-secondary)" : "none"),
                      "overflowX": "auto",
                      "overflowY": "hidden",
                      "borderTopLeftRadius": (showHeader ? "0px" : "8px"),
                      "borderTopRightRadius": (showHeader ? "0px" : "8px"),
                      "borderBottomLeftRadius": (canAddSubGroup ? (showHeader ? "0px" : "8px") : "8px"),
                      "borderBottomRightRadius": (canAddSubGroup ? (showHeader ? "0px" : "8px") : "8px")
                    }}>
                {redesignEnabled ? (
                  <div className="ticket-type-v2-table">
                    <TicketTypeTableHeader />
                    <SortableContext
                      items={v2TicketTypes.map(tt => tt.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <div ref={setNodeRef} className="ticket-type-v2-body">
                        {v2TicketTypes
                          .map((ticketType, index) => (
                            <TicketTypeRowV2
                              key={ticketType.id}
                              csrfToken={csrfToken}
                              team={team}
                              currentUser={currentUser}
                              confirm={confirm}
                              fetchNewTicketType={fetchNewTicketType}
                              ticketGroup={ticketGroup}
                              parentTicketGroup={parentTicketGroup}
                              ticketType={ticketType}
                              ticketTypeIsSeated={ticketTypeIsSeated}
                              canAddSubGroup={canAddSubGroup}
                              isLast={index === v2TicketTypes.length - 1}
                              ticketGroupChanged={ticketGroupChanged}
                              ticketTypesChanged={ticketTypesChanged}
                              fetchTicketTypeForEdit={fetchTicketTypeForEdit} />
                          ))}
                      </div>
                    </SortableContext>
                  </div>
                ) : (
                  <table
                    className="table my-0 responsive-card-table admissions-table"
                    style={{
                      color: "var(--sl-text-tertiary)",
                      border: "none",
                      fontSize: "14px",
                      background: "var(--sl-bg-primary)"
                    }}
                  >
                    <colgroup>
                      <col span={1} style={{width: "3%", minWidth: "20px"}} />
                      <col span={1} style={{width: "45%", minWidth: "350px"}} />
                      <col span={1} style={{width: "30%", minWidth: "240px"}} />
                      <col span={1} style={{width: "10%", minWidth: "60px"}} />
                      <col span={1} style={{width: "10%", minWdith: "100px"}} />
                      <col span={1} style={{width: "2%", minWidth: "20px"}} />
                    </colgroup>
                    <thead>
                      <tr
                        style={{
                          color: "var(--sl-text-quaternary)",
                          fontSize: "12px",
                          background: (isEven ? "var(--sl-bg-secondary)" : "var(--sl-bg-brand-primary)"),
                          borderBottom: groupBorder,
                          borderTop: (showHeader ? groupBorder : "none")
                        }}
                      >
                        <th className="px-1">&nbsp;</th>
                        <th style={{fontWeight: "600"}}>Ticket Name</th>
                        <th style={{fontWeight: "600"}}>On Sale Date</th>
                        <th style={{fontWeight: "600"}}>Price</th>
                        <th style={{fontWeight: "600"}}>Inventory</th>
                        <th style={{fontWeight: "600"}}>&nbsp;</th>
                      </tr>
                    </thead>
                    <SortableContext
                      items={sortedTicketTypes.map(tt => tt.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <tbody ref={setNodeRef}>
                        {sortedTicketTypes
                          .map((ticketType, index) => (
                            <TicketTypeRow
                              key={ticketType.id}
                              csrfToken={csrfToken}
                              team={team}
                              currentUser={currentUser}
                              confirm={confirm}
                              fetchNewTicketType={fetchNewTicketType}
                              ticketGroup={ticketGroup}
                              ticketType={ticketType}
                              ticketTypeIsSeated={ticketTypeIsSeated}
                              canAddSubGroup={canAddSubGroup}
                              isLast={index === ticketGroup.ticket_types.length - 1}
                              ticketGroupChanged={ticketGroupChanged}
                              ticketTypesChanged={ticketTypesChanged}
                              fetchTicketTypeForEdit={fetchTicketTypeForEdit} />
                          ))}
                      </tbody>
                    </SortableContext>
                  </table>
                )}
              </div>
            </div>
          </div>
          <DragDropContext onDragEnd={
                              (result) => {
                                if (!result.destination) {
                                  return false;
                                }

                                var itemIndex = result.source.index;
                                var moveIndex = result.destination.index;
                                var updated = Object.assign({}, ticketGroup.sub_groups[itemIndex], {
                                  position: (moveIndex + 1)
                                });

                                var stateCopy = [...ticketGroup.sub_groups];
                                var [removed] = stateCopy.splice(itemIndex, 1);
                                stateCopy.splice(moveIndex, 0, removed);

                                stateCopy = stateCopy.map((subGroup, index) => ({
                                  ...subGroup,
                                  position: index + 1
                                }));

                                ticketGroupChanged({
                                  ...ticketGroup,
                                  sub_groups: stateCopy
                                });

                                updateSubGroupPosition(updated);
                              }
                            }>
            <Droppable droppableId={`ticket-group-${ticketGroup.id}-sub-groups-droppable`}>
              {(provided, snapshot) => (
                <div {...provided.droppableProps} ref={provided.innerRef}>
                  {ticketGroup.sub_groups.sort((a, b) => a.position - b.position).map((subGroup, index) =>
                    <Draggable key={"" + subGroup.id} draggableId={"" + subGroup.id} index={index}>
                      {(provided, snapshot) => (
                        <TicketGroup csrfToken={csrfToken}
                                      team={team}
                                      currentUser={currentUser}
                                      fetchNewTicketType={fetchNewTicketType}
                                      fetchTicketTypeForEdit={fetchTicketTypeForEdit}
                                      ticketTypeIsSeated={ticketTypeIsSeated}
                                      confirm={confirm}
                                      depth={depth + 1}
                                      ticketGroupChanged={ticketGroupChanged}
                                      ticketTypesChanged={ticketTypesChanged}
                                      parentTicketGroup={ticketGroup}
                                      updateConfirm={updateConfirm}
                                      provided={provided}
                                      snapshot={snapshot}
                                      canAddSubGroup={canAddSubGroup && depth === 0}
                                      showHeader={showHeader || (showSubGroupHeaders && !subGroup.presale_pool)}
                                      rootTicketGroup={rootTicketGroup}
                                      redesignEnabled={redesignEnabled}
                                      ticketGroup={subGroup} />
                      )}
                    </Draggable>
                  )}
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
          {canAddSubGroup && !lockedPool ? (
            <div className={"card-footer border-0 " + (ticketGroup.root ? "text-left" : "text-right")}
                  style={{
                    "padding": (ticketGroup.root ? "12px 16px 24px 16px" : "12px 16px"),
                    "backgroundColor": "var(--sl-bg-primary)"
                  }}>
              <Button variant="link-color" icon={Plus} onClick={addSubGroup}>
                {ticketGroup.root ? "Add a group" : "Add Sub-group"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

TicketGroup.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  confirm: PropTypes.object.isRequired,
  ticketGroup: PropTypes.object.isRequired,
  fetchNewTicketType: PropTypes.func.isRequired,
  ticketTypeIsSeated: PropTypes.func.isRequired,
  fetchTicketTypeForEdit: PropTypes.func.isRequired,
  depth: PropTypes.number.isRequired,
  ticketGroupChanged: PropTypes.func.isRequired,
  ticketTypesChanged: PropTypes.func.isRequired,
  parentTicketGroup: PropTypes.object,
  updateConfirm: PropTypes.func.isRequired,
  provided: PropTypes.object,
  snapshot: PropTypes.object,
  canAddSubGroup: PropTypes.bool.isRequired,
  showHeader: PropTypes.bool.isRequired,
  showSubGroupHeaders: PropTypes.bool,
  rootTicketGroup: PropTypes.object.isRequired,
  redesignEnabled: PropTypes.bool
};

export default TicketGroup;
